import React, { useEffect, useState } from "react";
import Swal from 'sweetalert2';
import PropTypes from "prop-types";
import { Select } from 'antd';
import { EDIT_USER, GET_USERS } from "../../services/userServices";
import { GET_MANAGERS, GET_ROLES } from "../../services/commonServices";
import { fetchModules } from "../../services/authServices";
import { fetchWithAuth } from "../../utils/api1";
import { fetchDepartments } from "../../services/departmentsServices";

export default function EditUser({ editObj }) {

  const { Option } = Select;
  const [sessionUserData, setsessionUserData] = useState({});
  const [rolesData, setRolesData] = useState([]);
  const [editSubmitLoading, setEditSubmitLoading] = useState(false);
  const [manager, setManager] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [modules, SetModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [imagePreview, setImagePreview] = useState("");

  const [formData, setFormData] = useState({
    Id: "",
    RoleId: "",
    Name: "",
    Password: "",
    IsActive: true,
    OrgId: "",
    DeptId: "",
    Mobile: "",
    Email: "",
    IsMobile: false,
    Gender: "",
    ManagerId: "",
    IsSuperiorId: "",
    UpdatedBy: sessionUserData?.id,
    AccessToModules: "",
    ImageUrl: "",
  });


  const [usersData, setUsersData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    const userDataString = sessionStorage.getItem("userData");
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setsessionUserData(userData);
      setFormData((prev) => ({
        ...prev,
        OrgId: userData.OrgId,
        UpdatedBy: userData.Id
      }));
    }
  }, []);

  const getSessionData = (key) => {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  };

  const setSessionData = (key, data) => {
    sessionStorage.setItem(key, JSON.stringify(data));
  };

  const fetchData = async () => {
    const cacheKey = `users_${editObj?.OrgId}`;

    const cached = getSessionData(cacheKey);
    if (cached) {
      setUsersData(cached);
      return;
    }

    setDataLoading(true);
    try {
      const response = await fetchWithAuth(`${GET_USERS}?OrgId=${editObj?.OrgId}`);
      if (response.ok) {
        const data = await response.json();
        const result = data.ResultData || [];

        setUsersData(result);
        setSessionData(cacheKey, result); // ✅ save in session
      }
    } catch (error) {
      console.error("Error fetching users:", error.message);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (editObj?.OrgId) fetchData();
  }, [editObj?.OrgId]);

  const getModules = async () => {
    const cacheKey = "modules";

    const cached = getSessionData(cacheKey);
    if (cached) {
      SetModules(cached);
      return;
    }

    try {
      const data = await fetchModules();
      SetModules(data);
      setSessionData(cacheKey, data);
    } catch (error) {
      console.error("Failed to load modules:", error);
    }
  };

  useEffect(() => {
    if (editObj?.Id) getModules();
  }, [editObj?.Id]);

  const getDepartments = async () => {
    const cacheKey = `departments_${editObj?.OrgId}`;

    const cached = getSessionData(cacheKey);
    if (cached) {
      setDepartmentsData(cached);
      return;
    }

    try {
      const data = await fetchDepartments(editObj?.OrgId);
      setDepartmentsData(data);
      setSessionData(cacheKey, data);
    } catch (error) {
      console.error("Failed to load departments:", error);
    }
  };

  useEffect(() => {
    if (editObj?.OrgId) getDepartments();
  }, [editObj?.OrgId]); // ✅ fixed dependency

  const fetchManagerData = async () => {
    const cacheKey = `managers_${editObj?.OrgId}`;

    const cached = getSessionData(cacheKey);
    if (cached) {
      setManager(cached);
      return;
    }

    try {
      const response = await fetchWithAuth(`${GET_MANAGERS}?OrgId=${editObj?.OrgId}`);
      if (response.ok) {
        const data = await response.json();
        const result = data.ResultData || [];

        setManager(result);
        setSessionData(cacheKey, result);
      }
    } catch (error) {
      console.error("Error fetching managers:", error.message);
    }
  };

  useEffect(() => {
    if (editObj?.OrgId) fetchManagerData();
  }, [editObj?.OrgId]); // ✅ fixed


  // Fetch roles data
  const fetchRolesData = async () => {
    try {
      if (sessionUserData && sessionUserData?.OrgId) {
        const response = await fetchWithAuth(`${GET_ROLES}?OrgId=${editObj?.OrgId}`, {
          method: 'GET',
        });
        if (response.ok) {
          const data = await response.json();
          setRolesData(data.ResultData);
          // console.log(data.ResultData)
        } else {
          console.error("Failed to fetch roles:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error fetching roles:", error.message);
    }
  };

  useEffect(() => {
    fetchRolesData();
  }, [editObj]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        title: "Invalid File",
        text: "Only PNG, JPG, and JPEG files are allowed.",
        icon: "error",
      });
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ImageUrl: file,
    }));

    setImagePreview(URL.createObjectURL(file));
  };

  useEffect(() => {
    if (editObj) {
      setFormData({
        Id: editObj.Id,
        RoleId: editObj.RoleId || "",
        Name: editObj.Name || "",
        IsActive: editObj.IsActive ? 1 : 0,
        OrgId: editObj.OrgId || "",
        DeptId: editObj.DeptId || "",
        Mobile: editObj.Mobile || "",
        Email: editObj.Email || "",
        IsMobile: editObj.IsMobile ? 1 : 0,
        Gender: editObj.Gender ? 1 : 0,
        ManagerId: editObj.ManagerId || "",
        UpdatedBy: sessionUserData.Id,
        IsSuperiorId: editObj.IsSuperiorId || "",
        ImageUrl: "",
      });

      setImagePreview(editObj.ImageUrl || "");

      const parsedModules = editObj.AccessToModules
        ? editObj.AccessToModules.split(",").map((id) => parseInt(id.trim()))
        : [];

      setSelectedModules(parsedModules);
    }
  }, [editObj, sessionUserData.Id]);


  const handleInputChange = (e) => {
    setEmailError("");
    const { name, value, type, checked } = e.target;

    if (name === 'Mobile') {
      // Allow only digits, max 10 characters
      if (!/^\d{0,10}$/.test(value)) {
        Swal.fire({
          title: "Invalid Input",
          text: "Please enter only numbers (max 10 digits) without letters or special characters.",
          icon: "error",
        });
        return;
      }
    }

    const formattedValue = (name === 'Name' || name === 'City' || name === 'Department')
      ? toTitleCase(value)
      : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));
  };


  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.(com|in|gov|tech|info|org|net|us|edu|shop|dev)$/i;
    return regex.test(email);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitLoading(true);

    if (!/^\d{10}$/.test(formData.Mobile)) {
      Swal.fire({
        title: "Invalid Mobile Number",
        text: "Mobile number must be exactly 10 digits.",
        icon: "error",
      });
      setEditSubmitLoading(false);
      return;
    }

    if (!validateEmail(formData.Email)) {
      setEmailError("Please enter a valid email ending with ex: .com or .in");
      setEditSubmitLoading(false);
      return;
    }

    try {
      const payload = new FormData();

      payload.append("Id", formData.Id || "");
      payload.append("RoleId", formData.RoleId || "");
      payload.append("Name", formData.Name || "");
      payload.append("Password", formData.Password || "");
      payload.append("IsActive", 1);
      payload.append("OrgId", formData.OrgId || sessionUserData?.OrgId || "");
      payload.append("DeptId", formData.DeptId || "");
      payload.append("Mobile", formData.Mobile || "");
      payload.append("Email", formData.Email || "");
      payload.append("IsMobile", formData.IsMobile ? 1 : 0);
      payload.append("Gender", formData.Gender ?? "");
      payload.append("ManagerId", formData.ManagerId || "");
      payload.append("IsSuperiorId", formData.IsSuperiorId || "");
      payload.append("UpdatedBy", sessionUserData?.Id || "");
      payload.append("AccessToModules", selectedModules.join(","));

      if (formData.ImageUrl) {
        payload.append("ImageUrl", formData.ImageUrl);
      }

      const response = await fetchWithAuth(`${EDIT_USER}`, {
        method: "POST",
        body: payload,
      });

      if (response.ok) {
        const data = await response.json();

        if (data.ResultData?.Status === "Success") {
          Swal.fire({
            title: "Success",
            text: "User has been updated successfully.",
            icon: "success",
          });
        } else {
          Swal.fire({
            title: "Error",
            text: data?.ResultData?.ResultMessage || "Failed to update user.",
            icon: "error",
          });
        }
      } else {
        Swal.fire({
          title: "Error",
          text: "Failed to submit form.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error during submission:", error.message);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred.",
        icon: "error",
      });
    } finally {
      setEditSubmitLoading(false);
    }
  };

  const toTitleCase = (str) => {
    return str
      .split(/([.\s])/g) // Split and preserve dots and spaces
      .map(part =>
        /[a-zA-Z]/.test(part) ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part
      )
      .join('');
  };


  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="offcanvasRightEdit"
      aria-labelledby="offcanvasRightLabel"
      style={{ width: "90%" }}
    >
      <style>
        {`
          @media (min-width: 768px) { /* Medium devices and up (md) */
              #offcanvasRightEdit {
                  width: 50% !important;
              }
          }
        `}
      </style>
      <form onSubmit={handleSubmit}>
        <div className="offcanvas-header d-flex justify-content-between align-items-center">
          <h5 id="offcanvasRightLabel" className="mb-0">Edit User</h5>
          <div className="d-flex align-items-center">
            <button className="btn btn-primary btn-sm me-2" type="submit" disabled={editSubmitLoading}>
              {editSubmitLoading ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            ></button>
          </div>
        </div>
        <div className="offcanvas-body" style={{ marginTop: "-2rem", maxHeight: "42rem", overflowY: "auto" }}>
          <div className="row">
            <div className="col-6 mb-2">
              <label className="form-label">Name<span className="text-danger">*</span></label>
              <input
                type="text"
                name="Name"
                className="form-control"
                placeholder="Enter user name"
                value={formData.Name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="col-6 mb-2">
              <label className="form-label">
                Role <span className="text-danger">*</span>
              </label>
              <Select
                showSearch
                allowClear
                placeholder="Select Role"
                className="w-100"
                value={formData.RoleId || undefined}
                onChange={(value) => setFormData((prev) => ({ ...prev, RoleId: value }))}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                style={{ height: '2.4rem' }}
              >
                {rolesData?.map((role) => (
                  <Option key={role.Id} value={role.Id}>
                    {role.Name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">
                Department <span className="text-danger">*</span>
              </label>
              <Select
                showSearch
                allowClear
                placeholder="Select Department"
                className="w-100"
                value={formData.DeptId || undefined}
                onChange={(value) => setFormData((prev) => ({ ...prev, DeptId: value }))}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                style={{ height: '2.4rem' }}
              >
                {Array.isArray(departmentsData) &&
                  departmentsData.map((dep) => (
                    <Option key={dep.Id} value={dep.Id}>
                      {dep.DeptName}
                    </Option>
                  ))}

              </Select>
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">
                Superior <span className="text-danger">*</span>
              </label>
              <Select
                showSearch
                allowClear
                placeholder="Select Superior"
                className="w-100"
                value={formData.IsSuperiorId || undefined}
                onChange={(value) => setFormData((prev) => ({ ...prev, IsSuperiorId: value }))}
                optionFilterProp="children"
                loading={dataLoading}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                style={{ height: "2.4rem" }}
              >
                {Array.isArray(usersData) &&
                  usersData.map((user) => (
                    <Option key={user.Id} value={user.Id}>
                      {user.Name}
                    </Option>
                  ))}
              </Select>
            </div>


            <div className="col-6 mb-2">
              <label className="form-label">Mobile<span className="text-danger">*</span></label>
              <div className="input-group">
                <span className="input-group-text">🇮🇳 +91</span>
                <input
                  type="tel"
                  name="Mobile"
                  className="form-control"
                  placeholder="Enter 10-digit mobile number"
                  value={formData.Mobile}
                  onChange={handleInputChange}
                  maxLength={10}
                  required
                />
              </div>
            </div>

            <div className="col-6 mb-2">
              <label className="form-label">Email<span className="text-danger">*</span></label>
              <input
                type="email"
                name="Email"
                className={`form-control ${emailError ? 'is-invalid' : ''}`}
                placeholder="Enter email address"
                value={formData.Email}
                onChange={handleInputChange}
                required
              />
              {emailError && <div className="invalid-feedback">{emailError}</div>}
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Gender<span className="text-danger">*</span></label>
              <select
                className="form-select"
                name="Gender"
                value={formData.Gender}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Gender</option>
                <option value="1">Male</option>
                <option value="0">Female</option>
              </select>
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">
                Manager <span className="text-danger">*</span>
              </label>
              <Select
                showSearch
                allowClear
                placeholder="Select Manager"
                name="ManagerId"
                style={{ width: '100%', height: '2.4rem' }}
                value={formData.ManagerId || undefined}
                onChange={(value) => handleInputChange({ target: { name: 'ManagerId', value } })}
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {Array.isArray(manager) &&
                  manager.map((item) => (
                    <Option key={item.Id} value={item.Id}>
                      {item.Name}
                    </Option>
                  ))}
              </Select>
            </div>
            <div className="col-6 mb-2 d-flex flex-column">
              <label className="form-label">Modules<span className="text-danger">*</span></label>
              <Select
                mode="multiple"
                placeholder="Select Modules"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                value={selectedModules}
                onChange={(value) => setSelectedModules(value)}
                style={{ height: 'auto', minHeight: '2.4rem' }}
              >
                {modules?.map((item) => (
                  <Option key={item.Id} value={item.Id}>
                    {item.ModuleName}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Profile Image</label>
              <input
                type="file"
                name="ImageUrl"
                className="form-control"
                accept=".png,.jpg,.jpeg"
                onChange={handleImageChange}
              />
              <small className="text-muted">Only PNG, JPG, JPEG allowed</small>
            </div>
            <div className="col-12 mb-3">
              <label className="form-label">Image Preview</label>
              <div>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="User Preview"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "12px",
                      border: "1px solid #dee2e6",
                    }}
                  />
                ) : (
                  <div className="text-muted">No image available</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

EditUser.propTypes = {
  editObj: PropTypes.object.isRequired,
};
